import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { request } from '@/api/client';
import type {
  WebsiteImageRead,
  WebsiteKind,
  WebsiteRead,
  WebsiteSection,
  WebsiteWrite,
} from '@/api/schema';

type NewDraft = Pick<WebsiteWrite, 'kind' | 'title' | 'slug' | 'state'>;

const WEBSITE_KEY = ['website-content'];

export function useWebsiteSection(kind: WebsiteKind) {
  return useQuery({
    queryKey: [...WEBSITE_KEY, kind],
    queryFn: () => request<WebsiteSection>(`/api/v1/website/sections/${kind}`),
  });
}

export function useSaveWebsiteEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entry, id }: { entry: WebsiteWrite | NewDraft; id?: string }) =>
      request<WebsiteRead>(id ? `/api/v1/website/entries/${id}` : '/api/v1/website/entries', {
        method: id ? 'PUT' : 'POST',
        body: entry,
      }),
    retry: false,
    onSuccess: (entry) =>
      queryClient.invalidateQueries({ queryKey: [...WEBSITE_KEY, entry.kind] }),
  });
}

export function uploadWebsiteImage(entryId: string, image: File) {
  const formData = new FormData();
  formData.set('image', image);
  return request<WebsiteImageRead>(`/api/v1/website/entries/${entryId}/images`, {
    method: 'POST',
    formData,
  });
}
